/**
 * Which share tags a published page prints. Webflow set them page by page and
 * template by template, with no rule behind them, so they are listed here as
 * the published pages print them (a census of every page in the sitemap,
 * 2026-09-24):
 *
 * - `title`: `og:title` and `twitter:title`.
 * - `description`: `og:description` and `twitter:description`, and with them
 *   `og:type` and `twitter:card`, which no published page prints without them.
 * - `image`: `og:image` and `twitter:image`.
 * - `emptyDescription`: the template prints its description tags even for a
 *   page that has none, empty, where a page elsewhere prints no tag.
 *
 * Every other page prints all of them.
 */
type ShareTags = {
  title: boolean;
  description: boolean;
  image: boolean;
  emptyDescription: boolean;
};

const ALL: ShareTags = {
  title: true,
  description: true,
  image: true,
  emptyDescription: false,
};

/** Templates, by the path their pages sit under. */
const BY_PREFIX: ReadonlyArray<readonly [string, Partial<ShareTags>]> = [
  // The use-case pages publish their title and description and nothing else.
  ["/use-cases/", { title: false, description: false, image: false }],
  ["/proxy-locations/", { image: false }],
  ["/author/", { image: false, emptyDescription: true }],
  ["/blog-categories/", { image: false, emptyDescription: true }],
  ["/academy-article/", { emptyDescription: true }],
];

/** Single pages. */
const BY_PATH: Readonly<Record<string, Partial<ShareTags>>> = {
  "/chrome-proxy-extension": { title: false, description: false },
  "/intellectual-property": { description: false },
  "/webshares-ethically-sourced-residential-proxies": { description: false },
};

/** The share tags the page at `path` prints. */
export function shareTagsFor(path: string): ShareTags {
  const template = BY_PREFIX.find(([prefix]) => path.startsWith(prefix));
  return { ...ALL, ...template?.[1], ...BY_PATH[path] };
}
